import { isNil } from "lodash";
import { Utilization, WorkforceMetricsMode } from "PFApp/booking/types";
import { Threshold } from "PFTypes";
import { useCallback } from "react";

import { useBookingsOverviewContext } from "../context/bookings_overview_context";

type UseUtilizationValueProviderReturn = {
  getUtilizationValue: (utilization?: Utilization) => number | undefined;
  getUtilizationLabel: (value: number | undefined) => string | undefined;
  getBackgroundColor: (value: number, thresholds: Threshold[]) => string | undefined;
};

export const useUtilizationDataProvider = (): UseUtilizationValueProviderReturn => {
  const { workforceMetricsMode } = useBookingsOverviewContext();

  const getUtilizationValue = useCallback(
    (utilization: Utilization) => {
      if (!utilization) {
        return undefined;
      }
      const { billable_utilization: billableUtilization, utilization: totalUtilization } = utilization;
      switch (workforceMetricsMode) {
        case WorkforceMetricsMode.BillableUtilization:
          return billableUtilization;
        case WorkforceMetricsMode.TotalUtilization:
          return totalUtilization;
        default:
          return undefined;
      }
    },
    [workforceMetricsMode]
  );

  const getUtilizationLabel = useCallback(
    (value: number | undefined) => (!isNil(value) ? `${Math.round((value || 0) * 100)}%` : undefined),
    []
  );

  const getBackgroundColor = useCallback(
    (value: number, thresholds: Threshold[] = []): string | undefined =>
      thresholds?.find((item) => value * 100 >= item.min && value * 100 <= item.max)?.color,
    []
  );

  return {
    getUtilizationValue,
    getUtilizationLabel,
    getBackgroundColor
  };
};
